import React from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { Users, BookOpen, GraduationCap, Waves, Clock, Wind, Droplets, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { getCatLabel } from "./constants";
import { getLessonStatus, getLessonStatusLabel, getLessonStatusColor } from "../../lib/lessonUtils";
import LessonQRCode from "../LessonQRCode";

const AdminOverview = ({ stats, loading, weatherData }) => {
  const navigate = useNavigate();

  const getCatLabel = (cat) => cat === "surf" ? "Surf" : cat === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard";
  const getWeatherIcon = (code) => {
    if (code == null) return "~";
    if (code <= 1) return "\u2600\uFE0F";
    if (code <= 3) return "\u26C5";
    if (code <= 48) return "\u2601\uFE0F";
    if (code <= 67) return "\uD83C\uDF27\uFE0F";
    return "\u26C8\uFE0F";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#3d5a63] mb-2">Dashboard</h1>
        <p className="text-[#475569]">Visão geral da escola de surf</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <Card className="bg-white border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/students")} data-testid="admin-stat-students">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94A3B8] text-xs md:text-sm mb-1">Total Alunos</p>
                <p className="text-2xl md:text-3xl font-bold text-[#3d5a63]">{stats?.total_students || 0}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#6697a4]/10 flex items-center justify-center">
                <Users className="h-5 w-5 md:h-6 md:w-6 text-[#6697a4]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/lessons")} data-testid="admin-stat-enrolled">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94A3B8] text-xs md:text-sm mb-1">Inscritos (Futuro)</p>
                <p className="text-2xl md:text-3xl font-bold text-[#3d5a63]">{stats?.enrolled_future || 0}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-[#10B981]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/subscriptions")} data-testid="admin-stat-subscriptions">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94A3B8] text-xs md:text-sm mb-1">Subscrições</p>
                <p className="text-2xl md:text-3xl font-bold text-[#3d5a63]">{stats?.active_subscriptions || 0}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <GraduationCap className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/admin/subscriptions")} data-testid="admin-stat-overdue">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#94A3B8] text-xs md:text-sm mb-1">Em Atraso</p>
                <p className="text-2xl md:text-3xl font-bold text-red-600">{stats?.overdue_subscriptions || 0}</p>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <X className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weather Widget */}
      {weatherData && !weatherData.error && (
        <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-[#6697a4]/5 to-[#3d5a63]/5" data-testid="admin-weather-widget">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Waves className="h-4 w-4 text-[#6697a4]" />
              <p className="font-semibold text-sm text-[#3d5a63]">Condições do Mar — Apúlia</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl p-3 text-center">
                <Waves className="h-5 w-5 text-[#6697a4] mx-auto" />
                <p className="text-lg font-bold text-[#3d5a63] mt-1">{weatherData.wave_height != null ? `${weatherData.wave_height}m` : "—"}</p>
                <p className="text-[10px] text-[#94A3B8]">Ondas</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center">
                <Clock className="h-5 w-5 text-[#6697a4] mx-auto" />
                <p className="text-lg font-bold text-[#3d5a63] mt-1">{weatherData.wave_period != null ? `${weatherData.wave_period}s` : "—"}</p>
                <p className="text-[10px] text-[#94A3B8]">Período</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center">
                <Wind className="h-5 w-5 text-[#6697a4] mx-auto" />
                <p className="text-lg font-bold text-[#3d5a63] mt-1">{weatherData.wind_speed != null ? `${weatherData.wind_speed} km/h` : "—"}</p>
                <p className="text-[10px] text-[#94A3B8]">Vento</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center">
                <Droplets className="h-5 w-5 text-[#6697a4] mx-auto" />
                <p className="text-lg font-bold text-[#3d5a63] mt-1">{weatherData.water_temp != null ? `${Math.round(weatherData.water_temp)}°C` : "—"}</p>
                <p className="text-[10px] text-[#94A3B8]">Água</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Lessons */}
      <Card className="bg-white border-0 shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle>Próximas Aulas</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.upcoming_lessons?.length === 0 ? (
            <p className="text-[#94A3B8] text-center py-8">Sem aulas agendadas</p>
          ) : (
            <div className="space-y-6">
              {(() => {
                const grouped = {};
                stats?.upcoming_lessons?.forEach(l => {
                  const d = l.date;
                  if (!grouped[d]) grouped[d] = [];
                  grouped[d].push(l);
                });
                let globalIdx = 0;
                return Object.entries(grouped).map(([dateKey, dayLessons]) => (
                  <div key={dateKey}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">
                        {format(parseISO(dateKey), "EEEE, d MMM", { locale: pt })}
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <div className="space-y-5">
                      {dayLessons.map((lesson) => {
                        const idx = globalIdx++;
                        const status = getLessonStatus(lesson);
                        const isHighlighted = idx === 0;
                        return (
                          <div key={lesson.lesson_id} className={`p-4 rounded-xl cursor-pointer transition-all ${isHighlighted ? "bg-[#6697a4]/10 ring-2 ring-[#6697a4]/30 hover:bg-[#6697a4]/15" : "bg-slate-50 hover:bg-slate-100"}`} onClick={() => navigate("/admin/lessons")} data-testid={`overview-lesson-${lesson.lesson_id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center ${isHighlighted ? "bg-[#6697a4] text-white" : "bg-[#6697a4]/10"}`}>
                                  <Waves className={`h-5 w-5 ${isHighlighted ? "text-white" : "text-[#6697a4]"}`} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-[#3d5a63] truncate">{getCatLabel(lesson.lesson_category)}</p>
                                    {isHighlighted && <Badge className={`${getLessonStatusColor(status)} border-0 text-[10px]`}>{getLessonStatusLabel(lesson)}</Badge>}
                                  </div>
                                  <p className="text-sm text-[#94A3B8]">
                                    {lesson.start_time} - {lesson.end_time}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline" className="border-[#6697a4] text-[#6697a4] shrink-0">
                                {lesson.current_enrollments}/{lesson.max_students}
                              </Badge>
                            </div>
                            {isHighlighted && (
                              <div className="mt-3 flex items-center justify-end gap-2">
                                <span onClick={(e) => e.stopPropagation()}>
                                  <LessonQRCode lessonId={lesson.lesson_id} lessonTitle={getCatLabel(lesson.lesson_category)} />
                                </span>
                                <Button size="sm" className="bg-[#6697a4] hover:bg-[#557f8a] text-white rounded-full h-8 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/admin/lessons?expand=${lesson.lesson_id}`); }} data-testid="quick-access-lesson">
                                  Gerir Alunos
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOverview;
